import { Col, Container, Row } from "reactstrap";
import s from "../organizationSetup/styles.scss";
import WizardBreadcrumb from "../common/WizardBreadcrumb";
import React from "react";
import { Wizard } from "react-use-wizard";
import WizardStepper from "../components/WizardStepper";
import StepTransition from "../components/StepTransition";
import { WizardStep } from "../common/types";
import withStyles from "isomorphic-style-loader/withStyles";
import DomainIcon from "@mui/icons-material/Domain";
import SettingsIcon from "@mui/icons-material/Settings";
import EmailIcon from "@mui/icons-material/Email";
import DomainInputStep, { domainInputStepSchema } from "./domainInputStep";
import DnsConfigurationStep, { dnsConfigurationStepSchema } from "./dnsConfigurationStep";
import SendingDomainsStep, { sendingDomainsSubmissionSchema } from "./sendingDomainsStep";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import { connect } from "react-redux";
import LoadingSpinner from "../../../../../toolkit/loadingSpinner";
import SnackbarNotification from "../../../../../toolkit/snackbarNotification";
import { useEmailSetupData } from "./hooks/useEmailSetupData";
import { useSnackbar } from "../../../../../hooks/useSnackbar";

export const wizardSteps: WizardStep[] = [
  {
    label: "Connect Domain",
    icon: <DomainIcon fontSize="small" />,
    Component: DomainInputStep,
    schema: domainInputStepSchema,
  },
  {
    label: "Configure DNS",
    icon: <SettingsIcon fontSize="small" />,
    Component: DnsConfigurationStep,
    schema: dnsConfigurationStepSchema,
  },
  {
    label: "Sending Domains",
    icon: <EmailIcon fontSize="small" />,
    Component: SendingDomainsStep,
    schema: sendingDomainsSubmissionSchema,
  },
];

const domainConfigurationWizardSchema = yup.object().shape(
  wizardSteps.reduce((acc, step) => ({
    ...acc,
    ...step.schema.fields
  }), {})
);

interface DomainConfigurationWizardProps extends ServiceCallerProps {
  embedded?: boolean;
  onExit?: () => void;
  charityId?: string;
}

const DomainConfigurationWizard: React.FC<DomainConfigurationWizardProps> = ({
  embedded = true,
  onExit,
  charityId,
  config,
  serviceAccessToken,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();

  const methods = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(domainConfigurationWizardSchema),
    defaultValues: {
      sendingDomain: "",
      domainResponse: null,
      sendingDomains: [],
      tempSenderName: "",
      tempSenderEmail: "",
    },
  });

  const { loading, startingStep, emailSetupData, existingSendingEmails, error } = useEmailSetupData({
    charityId,
    adminUrl: config?.adminServiceUrl,
    serviceAccessToken,
  });

  const renderMainContent = () => {
    if (error) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="text-center">
            <h5 className="text-danger">Unable to load email setup</h5>
            <p className="text-muted">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <FormProvider {...methods}>
        <form noValidate className={s.fadeIn}>
          <Wizard
            startIndex={startingStep}
            header={
              <WizardStepper
                steps={wizardSteps}
                onBack={onExit}
                showBack={!embedded}
              />
            }
            wrapper={
              <StepTransition
                contentClassName={embedded ? undefined : s.wizardStepPanel}
              />
            }
          >
            {wizardSteps.map(({ Component }, index) => (
              <Component
                key={index}
                charityId={charityId}
                config={config}
                serviceAccessToken={serviceAccessToken}
                emailSetupData={emailSetupData}
                existingSendingEmails={existingSendingEmails}
                onExit={onExit}
                showSnackbar={showSnackbar}
              />
            ))}
          </Wizard>
        </form>
      </FormProvider>
    );
  };

  return (
    <>
      <Container fluid className={embedded ? "vh-100" : s.fullScreenContainer}>
        <Row className="h-100">
          <Col md="12" className={s.wizardContainer}>
            {!embedded && loading && (
              <WizardBreadcrumb onBack={onExit} showBreadcrumb />
            )}
            {loading ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading Email Setup"
                  message="Please wait while we load your data and initialize the wizard."
                  className={s.fadeIn}
                />
              </div>
            ) : (
              <div className={embedded ? undefined : s.wizardShell}>
                {renderMainContent()}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
  charityId: state.user.currentCharity?.id,
});

export default connect(mapStateToProps)(
  serviceCaller(withStyles(s)(DomainConfigurationWizard))
);
